import { withGTM } from '@/features/shared/HOCs';
import {
  resolveBool,
  resolveColorName,
  resolveImage,
  resolveLink,
  resolveNumber,
  resolveRTC,
  resolveSpacingSize,
  resolveVideo,
} from '@/utils/resolvers';
import React, { memo, useRef } from 'react';
import CMSWrapper from '../shared/CMSWrapper';
import Hero from './Hero';
import { mapTarget, onDatalayer } from './Hero.datalayer';
import { useVisibility } from '@/hooks/useVisibility';
import CMSPlaceholder from '@/features/shared/CMSPlaceholder';

const resolveAnimate = ({ rawVal }: any) => {
  const { animatedSegment: segments, staticHeadlineSegment } = rawVal || {};
  return {
    segments: Array.isArray(segments)
      ? segments.map((s: any) => ({ label: s.string, pattern: s.dynamicdropdown }))
      : [],
    staticHeadline: resolveRTC({ rawVal: staticHeadlineSegment }),
  };
};

const MappedHero = memo(({ content, presentation, contentButtons, isPreview, labels }: any) => {
  const { isVisible } = useVisibility(presentation?.visibility as VisibilityVariant);

  if (!isVisible) {
    if (isPreview) {
      return (
        <CMSPlaceholder
          componentName="Hero Component"
          message={`This component is only visible on ${presentation?.visibility}`}
        />
      );
    }
    return null;
  }

  return (
    <div data-datalayer-position="hero" data-datalayer-variant={presentation?.datalayerVariant}>
      <Hero
        content={content}
        presentation={presentation}
        contentButtons={contentButtons}
        previewMode={isPreview}
        labels={labels}
      />
    </div>
  );
});

const MappedHeroGTM = withGTM({
  Component: MappedHero,
  onDatalayer,
  config: { mapTarget },
});

const HeroCMSWrapper = (brProps: Br.Props) => {
  const mapping = useRef({
    document: [
      'content',
      {
        logo: [null, resolveImage],
        headline: [null, resolveRTC],
        subHeading: ['subheading', resolveRTC],
        superheading: null,
        primaryCTA: [null, resolveLink],
        secondaryCTA: [null, resolveLink],
        backgroundImage: [null, resolveImage],
        mobileBackgroundImage: [null, resolveImage],
        backgroundVideo: [null, resolveVideo],
        animatedHeading: [null, resolveAnimate],
      },
    ],
    'heroComponent.labels': 'labels',
    componentParameterMap: [
      'presentation',
      {
        template: null,
        'content-alignment': 'contentAlignment',
        'text-area-shadow': ['hasTextShadow', resolveBool],
        'beneath-navigation': ['isBeneathNavigation', resolveBool],
        'background-overlay-opacity': ['backgroundOverlayOpacity', resolveNumber],
        'bottom-spacing': ['containerBottomSpacing', resolveSpacingSize],
        'container-bottom-background-color': ['containerBottomBackgroundColor', resolveColorName],
        'text-color': 'textColor',
        'include-jumplinks': ['includeJumplinks', resolveBool],
        'background-color': 'backgroundColor',
        parallax: ['isParallax', resolveBool],
        'content-overflow': ['overflowContent', resolveBool],
        'variant-label': 'datalayerVariant',
        visibility: 'visibility',
      },
    ],
  });

  const requiredFields = useRef({
    document: '<b>Hero Content</b> required',
  });

  const contentButtonNames = useRef({
    document: 'content',
  });

  return (
    <CMSWrapper
      brProps={brProps}
      render={MappedHeroGTM}
      mapping={mapping.current}
      requiredFields={requiredFields.current}
      contentButtonNames={contentButtonNames.current}
    />
  );
};

export default HeroCMSWrapper;
